/**
 * AddNpiDialog — search NPI registry then add provider to tenant.
 *
 * Two-step flow:
 *   1. User enters 10-digit NPI → POST /providers/lookup → show name/taxonomy
 *   2. User clicks "Add now" → POST /providers → on success calls onAdded()
 *
 * The dialog manages all of its own state and surfaces vendor errors verbatim
 * (using the same readVendorError pattern from AuthPage).
 */
import { useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Loader2, X, Search, CheckCircle2, AlertTriangle } from 'lucide-react';
import { lookupNpi, addProvider } from '../../lib/api/ediApi';

function readVendorError(data, fallback) {
  const d = data?.detail;
  if (typeof d === 'string' && d) return d;
  if (Array.isArray(d) && d.length) {
    const msgs = d.map((e) => e?.msg || e?.message).filter(Boolean);
    if (msgs.length) return msgs.join('; ');
  }
  if (d && typeof d === 'object') {
    const issues = Array.isArray(d.issues) ? d.issues.filter(Boolean) : [];
    if (d.message && issues.length) return `${d.message}: ${issues.join('; ')}`;
    if (d.message) return d.message;
  }
  if (typeof data?.message === 'string' && data.message) return data.message;
  return fallback;
}

const AddNpiDialog = ({ token, onClose, onAdded }) => {
  const [npi, setNpi] = useState('');
  const [looking, setLooking] = useState(false);
  const [adding, setAdding] = useState(false);
  const [result, setResult] = useState(null); // { npi_number, first_name, last_name, taxonomy, ... }
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleSearch = async (e) => {
    e?.preventDefault();
    setError('');
    setResult(null);
    setSuccess(false);
    if (!/^\d{10}$/.test(npi)) {
      setError('NPI must be exactly 10 digits.');
      return;
    }
    setLooking(true);
    try {
      const { ok, status, data } = await lookupNpi(token, npi);
      if (!ok) {
        setError(readVendorError(data, `Lookup failed (HTTP ${status})`));
        return;
      }
      // The vendor returns either the provider object at top level or under .provider/.result.
      // Be liberal in what we accept.
      const provider = data?.provider || data?.result || data;
      if (!provider || !provider.npi_number) {
        setError('Lookup returned no provider details.');
        return;
      }
      setResult(provider);
    } catch (err) {
      setError(err.message || 'Lookup failed');
    } finally {
      setLooking(false);
    }
  };

  const handleAdd = async () => {
    if (!result) return;
    setAdding(true);
    setError('');
    try {
      // Vendor only needs npi_number — it pulls provider_name, taxonomies,
      // address etc. from its own NPPES cache.
      const payload = { npi_number: result.npi_number };
      const { ok, status, data } = await addProvider(token, payload);
      if (!ok) {
        setError(readVendorError(data, `Add provider failed (HTTP ${status})`));
        return;
      }
      setSuccess(true);
      // Notify parent so the metrics row can refresh, but keep the modal open
      // for ~900ms so the user sees the confirmation.
      onAdded?.();
      setTimeout(() => { onClose?.(); }, 900);
    } catch (err) {
      setError(err.message || 'Add provider failed');
    } finally {
      setAdding(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[1000] bg-black/50 flex items-center justify-center p-4"
      data-testid="add-npi-dialog"
    >
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden flex flex-col">
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200">
          <p className="text-sm font-semibold text-slate-900">Add NPI</p>
          <button
            onClick={() => { if (!adding) onClose?.(); }}
            disabled={adding}
            className="text-slate-400 hover:text-slate-700 transition-colors disabled:opacity-30"
            data-testid="add-npi-close"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {/* Search */}
          <form onSubmit={handleSearch} className="space-y-2">
            <label className="text-[12px] text-slate-600 font-medium">NPI number</label>
            <div className="flex gap-2">
              <Input
                value={npi}
                onChange={(e) => setNpi(e.target.value.replace(/\D/g, '').slice(0, 10))}
                placeholder="1234567890"
                inputMode="numeric"
                maxLength={10}
                disabled={looking || adding}
                className="font-mono text-sm"
                data-testid="add-npi-input"
              />
              <Button
                type="submit"
                disabled={looking || adding || npi.length !== 10}
                className="bg-slate-900 hover:bg-slate-800 gap-1.5"
                data-testid="add-npi-search"
              >
                {looking ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Search className="h-3.5 w-3.5" />}
                Search
              </Button>
            </div>
            <p className="text-[10px] text-slate-400">
              POST /api/edi/note-to-claim/providers/lookup
            </p>
          </form>

          {/* Result */}
          {result && (
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 space-y-2" data-testid="add-npi-result">
              <div className="flex items-start justify-between gap-2">
                <p className="text-[10px] text-slate-500 uppercase tracking-wide">Registry match</p>
                {result.provider_type && (
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-200 text-slate-700 font-medium">
                    {result.provider_type}
                  </span>
                )}
              </div>
              <p className="text-sm font-semibold text-slate-900" data-testid="add-npi-result-name">
                {result.provider_name || [result.first_name, result.last_name].filter(Boolean).join(' ') || result.name || '—'}
              </p>
              <p className="text-[11px] font-mono text-slate-600">NPI {result.npi_number}</p>

              {Array.isArray(result.taxonomies) && result.taxonomies.length > 0 && (
                <div className="pt-1 space-y-1">
                  {result.taxonomies.map((t, i) => (
                    <div key={i} className="text-[11px] text-slate-600 flex items-start gap-1.5" data-testid={`add-npi-taxonomy-${i}`}>
                      {t.primary && (
                        <span className="text-[9px] px-1 py-px rounded bg-emerald-100 text-emerald-700 font-semibold mt-0.5 shrink-0">
                          PRIMARY
                        </span>
                      )}
                      <span className="font-mono text-slate-500">{t.code}</span>
                      <span className="text-slate-700">·</span>
                      <span>{t.desc}</span>
                      {t.license && <span className="text-slate-400">· lic {t.license}</span>}
                    </div>
                  ))}
                </div>
              )}

              {result.address && (
                <div className="pt-1 text-[11px] text-slate-500 leading-snug" data-testid="add-npi-result-address">
                  {result.address.address_1}
                  {result.address.address_2 ? `, ${result.address.address_2}` : ''}
                  <br />
                  {[result.address.city, result.address.state, result.address.postal_code]
                    .filter(Boolean).join(', ')}
                  {result.address.telephone && (
                    <span className="block text-slate-400">{result.address.telephone}</span>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Success */}
          {success && (
            <div className="flex items-center gap-2 rounded-lg bg-emerald-50 border border-emerald-200 px-3 py-2 text-[11px] text-emerald-700" data-testid="add-npi-success">
              <CheckCircle2 className="h-3.5 w-3.5" />
              Provider added.
            </div>
          )}

          {/* Error */}
          {error && (
            <div className="flex items-start gap-2 rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-[11px] text-red-700" data-testid="add-npi-error">
              <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {/* Add now */}
          {result && !success && (
            <Button
              type="button"
              onClick={handleAdd}
              disabled={adding}
              className="w-full bg-emerald-600 hover:bg-emerald-700 gap-1.5"
              data-testid="add-npi-confirm"
            >
              {adding ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
              Add now
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default AddNpiDialog;
